// Arjuna Orbital Edge AI — client-side mission simulator.
// Pure functions + a reducer. Not flight software. All values are simulated.

const R_KM = 6371; // Earth
const ORBIT_ALT_KM = 500;
const ORBIT_VEL_KMS = 7.612;
const DEG = Math.PI / 180;

export const GROUND_STATIONS = [
  { id: "gs-pune", name: "Pune GS", lat: 18.52, lon: 73.86, radius_km: 1800 },
  { id: "gs-sriharikota", name: "Sriharikota GS", lat: 13.72, lon: 80.23, radius_km: 1800 },
];

export const DEMO_REGIONS = [
  { id: "western-ghats", name: "Western Ghats", lat: 15.3, lon: 74.3, type: "forest" },
  { id: "pune", name: "Pune", lat: 18.52, lon: 73.86, type: "infra" },
  { id: "gargai-dam", name: "Gargai Dam", lat: 19.75, lon: 73.19, type: "water" },
  { id: "sundarbans", name: "Sundarbans", lat: 21.95, lon: 88.9, type: "forest" },
  { id: "punjab-fields", name: "Punjab Fields", lat: 30.9, lon: 75.85, type: "agri" },
];

export const AI_MODELS = [
  { id: "fire-net", name: "FireNet v3", task: "fire", avg_ms: 240, class: "Thermal anomaly / fire" },
  { id: "canopy-net", name: "CanopyNet v2", task: "forest-change", avg_ms: 320, class: "Forest cover change" },
  { id: "flood-net", name: "FloodNet v1", task: "flood", avg_ms: 280, class: "Flood extent" },
  { id: "agri-net", name: "AgriStressNet v2", task: "agri", avg_ms: 210, class: "Crop vegetation stress" },
  { id: "infra-net", name: "InfraΔNet v1", task: "infra", avg_ms: 300, class: "Infrastructure change" },
];

export const SENSORS = ["Optical RGB", "Multispectral", "SAR C-band", "Thermal IR"];

// Deterministic PRNG (so demos are reproducible)
function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Haversine
export function distKm(a, b) {
  const dLat = (b.lat - a.lat) * DEG;
  const dLon = (b.lon - a.lon) * DEG;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * DEG) * Math.cos(b.lat * DEG) * Math.sin(dLon / 2) ** 2;
  return 2 * R_KM * Math.asin(Math.sqrt(s));
}

// Propagate a toy sun-synchronous-ish orbit. Angular velocity around Earth centre.
export function satPosAt(tSec, inclinationDeg = 65, raanDeg = 20) {
  const period = (2 * Math.PI * (R_KM + ORBIT_ALT_KM)) / ORBIT_VEL_KMS; // s
  const theta = (2 * Math.PI * tSec) / period; // true anomaly
  const inc = inclinationDeg * DEG;
  // Satellite in orbit plane
  const x = Math.cos(theta);
  const y = Math.sin(theta) * Math.cos(inc);
  const z = Math.sin(theta) * Math.sin(inc);
  // Rotate by RAAN
  const r = raanDeg * DEG;
  const xp = x * Math.cos(r) - y * Math.sin(r);
  const yp = x * Math.sin(r) + y * Math.cos(r);
  const zp = z;
  const lat = Math.asin(zp) / DEG;
  let lon = Math.atan2(yp, xp) / DEG;
  // Earth rotation (approx. 15°/hour -> 0.004167°/s)
  lon -= tSec * 0.004167;
  // normalise
  lon = ((lon + 540) % 360) - 180;
  return { lat, lon };
}

// Footprint radius (very rough, ~900 km swath -> 450 radius)
export const FOOTPRINT_KM = 450;

export function inFootprint(pos, target) {
  return distKm(pos, target) < FOOTPRINT_KM;
}

export function inContact(satPos) {
  return GROUND_STATIONS.find((gs) => distKm(satPos, gs) < gs.radius_km) || null;
}

// Build an observation (synthetic payload)
export function makeObservation(sim, region, forcedKind = null) {
  const rand = mulberry32(sim.seed + sim.obs_counter * 7919);
  const kinds = ["fire", "forest-change", "flood", "agri", "infra"];
  // Bias by region type for realism
  const typePref = {
    forest: ["forest-change", "fire"],
    water: ["flood"],
    agri: ["agri"],
    infra: ["infra"],
  };
  const prefs = typePref[region.type] || kinds;
  const kind = forcedKind || (rand() < 0.7 ? prefs[Math.floor(rand() * prefs.length)] : kinds[Math.floor(rand() * kinds.length)]);
  const model = AI_MODELS.find((m) => m.task === kind) || AI_MODELS[0];
  const sensor = SENSORS[Math.floor(rand() * SENSORS.length)];
  const raw_mb = 180 + Math.floor(rand() * 720); // 180-900 MB
  const inf_ms = Math.round(model.avg_ms * (0.75 + rand() * 0.6));
  const conf = 0.55 + rand() * 0.44;
  const severityScore = conf * (kind === "fire" ? 1.0 : kind === "flood" ? 0.85 : kind === "forest-change" ? 0.7 : kind === "infra" ? 0.65 : 0.55);
  const severity = severityScore > 0.8 ? "critical" : severityScore > 0.6 ? "high" : severityScore > 0.45 ? "medium" : "low";
  const retain = severity === "critical" || severity === "high";
  const out_kb = retain ? Math.round(80 + rand() * 180) : Math.round(10 + rand() * 40); // thumb+metadata vs tiny marker
  const now = sim.sim_t;
  return {
    id: `OBS-${String(sim.obs_counter + 1).padStart(4, "0")}`,
    ts: now,
    region: region.name,
    lat: region.lat + (rand() - 0.5) * 0.4,
    lon: region.lon + (rand() - 0.5) * 0.4,
    sensor,
    model: model.name,
    model_id: model.id,
    kind,
    class: model.class,
    raw_mb,
    inference_ms: inf_ms,
    confidence: Math.round(conf * 100) / 100,
    severity,
    priority: ["low", "medium", "high", "critical"].indexOf(severity),
    out_kb,
    stage: "preprocess",
    retained: retain,
    downlinked: false,
  };
}

const INITIAL_RESOURCES = {
  cpu_pct: 12,
  cpu_limit: 85,
  ram_mb: 420,
  ram_limit: 2048,
  storage_mb: 2200,
  storage_limit: 32768,
  power_w: 28,
  power_limit: 90,
  temp_c: 24,
  temp_limit: 55,
  bandwidth_mbps: 12,
};

export const INITIAL_STATE = {
  mission_id: "AORBX-001",
  sat_name: "AORBX-1",
  seed: 42,
  sim_t: 0,
  running: false,
  speed: 1, // sim-sec per tick
  state: "nominal", // nominal | imaging | processing | downlink
  pos: satPosAt(0),
  resources: INITIAL_RESOURCES,
  observations: [],
  events: [],
  queue: [],
  downlinked: [],
  log: [],
  obs_counter: 0,
  current_target: null,
  conv_metrics: { raw_sent_mb: 0, time_to_deliver_s: 0 },
  edge_metrics: { out_sent_mb: 0, time_to_detect_s: 0 },
  contact_override: null, // null | 'force-off' | 'force-on'
  bandwidth: 12,
  policy: "severity-first", // severity-first | fifo | age
};

export function reducer(state, action) {
  switch (action.type) {
    case "set_running":
      return { ...state, running: action.running };
    case "set_speed":
      return { ...state, speed: action.speed };
    case "set_bandwidth":
      return { ...state, bandwidth: action.v, resources: { ...state.resources, bandwidth_mbps: action.v } };
    case "set_policy":
      return log(state, `Policy changed to '${action.v}'`, { ...state, policy: action.v });
    case "set_cpu_limit":
      return { ...state, resources: { ...state.resources, cpu_limit: action.v } };
    case "contact_override":
      return log(state, `Ground contact override. ${action.v || "auto"}`, { ...state, contact_override: action.v });
    case "reset":
      return { ...INITIAL_STATE, seed: Math.floor(Math.random() * 1e6) };
    case "trigger_obs": {
      const region = action.region || DEMO_REGIONS[Math.floor(Math.random() * DEMO_REGIONS.length)];
      const obs = makeObservation(state, region, action.kind || null);
      let s = { ...state, obs_counter: state.obs_counter + 1, observations: [obs, ...state.observations].slice(0, 300), state: "imaging" };
      s = log(s, `Observation ${obs.id} acquired. ${obs.region} (${obs.sensor})`, s);
      return s;
    }
    case "tick":
      return tick(state);
    default:
      return state;
  }
}

function log(prev, msg, next) {
  const entry = { t: next.sim_t, msg };
  const nextLog = [entry, ...(next.log || prev.log || [])].slice(0, 400);
  return { ...next, log: nextLog };
}

// Core simulation step
function tick(prev) {
  if (!prev.running) return prev;
  const dt = prev.speed;
  const sim_t = prev.sim_t + dt;
  const pos = satPosAt(sim_t);

  let state = { ...prev, sim_t, pos };

  // Contact window
  const gsAuto = inContact(pos);
  const inGsContact =
    state.contact_override === "force-off"
      ? null
      : state.contact_override === "force-on"
      ? GROUND_STATIONS[0]
      : gsAuto;

  // Auto-generate observations when near demo regions
  if (Math.random() < 0.18) {
    const nearest = [...DEMO_REGIONS].sort((a, b) => distKm(pos, a) - distKm(pos, b))[0];
    if (distKm(pos, nearest) < 1600 && state.observations.length < 300) {
      const obs = makeObservation(state, nearest);
      state.observations = [obs, ...state.observations].slice(0, 300);
      state.obs_counter += 1;
      state.state = "imaging";
      state = log(state, `Auto obs ${obs.id}. ${nearest.name}`, state);
    }
  }

  // Progress pipeline for pending observations
  const stages = ["preprocess", "quality", "inference", "classify", "prioritize", "compress", "queue"];
  let r = { ...state.resources };
  const obs = state.observations.map((o) => {
    if (!stages.includes(o.stage) || o.stage === "queue") return o;
    // Each tick, advance one stage per obs, consuming resources
    if (r.cpu_pct + 8 > r.cpu_limit) return o; // defer
    r.cpu_pct = Math.min(r.cpu_limit, r.cpu_pct + 8);
    r.ram_mb = Math.min(r.ram_limit, r.ram_mb + 48);
    r.power_w = Math.min(r.power_limit, r.power_w + 2);
    r.temp_c = Math.min(r.temp_limit, r.temp_c + 0.3);
    const idx = stages.indexOf(o.stage);
    const nextStage = stages[idx + 1];
    return { ...o, stage: nextStage };
  });

  // Resource cooldown
  r.cpu_pct = Math.max(10, r.cpu_pct - 4);
  r.ram_mb = Math.max(380, r.ram_mb - 20);
  r.power_w = Math.max(26, r.power_w - 1);
  r.temp_c = Math.max(22, r.temp_c - 0.2);

  state.resources = r;
  state.observations = obs;

  // Promote queued observations into mission queue (if retained) or discard low-priority (dropped)
  const newQueueItems = [];
  const nextObs = obs.map((o) => {
    if (o.stage === "queue" && !state.queue.find((q) => q.id === o.id) && !o.downlinked) {
      newQueueItems.push({
        id: o.id,
        type: o.class,
        kind: o.kind,
        size_kb: o.retained ? o.out_kb : Math.round(o.raw_mb * 1024), // if not processed, raw size
        priority: o.priority,
        severity: o.severity,
        created_t: o.ts,
        state: inGsContact ? "ready" : "stored",
        retries: 0,
        retain_until_s: 3600,
        lat: o.lat,
        lon: o.lon,
      });
      // Create event
      state.events = [
        {
          id: `EV-${o.id.slice(4)}`,
          type: o.class,
          kind: o.kind,
          lat: o.lat,
          lon: o.lon,
          region: o.region,
          ts: o.ts,
          confidence: o.confidence,
          severity: o.severity,
          source_obs: o.id,
          status: "detected",
          downlink: "pending",
          action: o.severity === "critical" ? "Alert operations + notify stakeholders" : o.severity === "high" ? "Monitor & schedule follow-up" : "Archive",
        },
        ...state.events,
      ].slice(0, 500);
      // Edge metric: time to detect = instant on edge
      state.edge_metrics = {
        ...state.edge_metrics,
        out_sent_mb: state.edge_metrics.out_sent_mb, // added on downlink
        time_to_detect_s: 0,
      };
      // Conv metric: full raw stays on satellite — not transmitted until contact
      state.conv_metrics = { ...state.conv_metrics };
    }
    return o;
  });
  state.observations = nextObs;
  state.queue = [...newQueueItems.sort((a, b) => b.priority - a.priority), ...state.queue].slice(0, 500);

  // Downlink if in contact
  if (inGsContact && state.queue.length > 0) {
    state.state = "downlink";
    // Order by policy
    let ordered = [...state.queue];
    if (state.policy === "severity-first") ordered.sort((a, b) => b.priority - a.priority || a.created_t - b.created_t);
    else if (state.policy === "fifo") ordered.sort((a, b) => a.created_t - b.created_t);
    else if (state.policy === "age") ordered.sort((a, b) => a.created_t - b.created_t);

    // Transmit as much as bandwidth allows this tick
    let budget_kb = (state.bandwidth * 1024) / 8; // Mbps -> KBps approx (1 Mbps ≈ 125 KB/s)
    budget_kb *= dt;
    const remaining = [];
    for (const q of ordered) {
      if (budget_kb <= 0) {
        remaining.push(q);
        continue;
      }
      if (q.size_kb <= budget_kb) {
        budget_kb -= q.size_kb;
        state.downlinked = [
          { ...q, downlinked_t: sim_t, ground_station: inGsContact.name },
          ...state.downlinked,
        ].slice(0, 500);
        // Mark event downlink status
        state.events = state.events.map((e) =>
          e.source_obs === q.id ? { ...e, status: "downlinked", downlink: "complete" } : e
        );
        // Metrics
        if (q.priority >= 2) state.edge_metrics.out_sent_mb += q.size_kb / 1024;
        else state.conv_metrics.raw_sent_mb += q.size_kb / 1024;
        state = log(state, `Downlinked ${q.id} to ${inGsContact.name} (${q.size_kb} kb, ${q.severity})`, state);
      } else {
        // Partial — defer
        remaining.push(q);
      }
    }
    state.queue = remaining;
  } else if (state.observations.some((o) => o.stage !== "queue")) {
    state.state = "processing";
  } else {
    state.state = "nominal";
  }

  return state;
}

// Derived metrics
export function metrics(state) {
  const raw_input_mb = state.observations.reduce((s, o) => s + o.raw_mb, 0);
  const processed_out_mb = state.queue.reduce((s, q) => s + q.size_kb / 1024, 0) + state.downlinked.reduce((s, d) => s + d.size_kb / 1024, 0);
  const reduction_pct = raw_input_mb > 0 ? (1 - processed_out_mb / raw_input_mb) * 100 : 0;
  const high_pri_sent = state.downlinked.filter((d) => d.priority >= 2).length;
  const low_pri_deferred = state.queue.filter((q) => q.priority < 2).length;
  const detections = state.events.length;
  // Conventional projected: all raw bytes would travel
  const conv_total_mb = raw_input_mb;
  const edge_total_mb = processed_out_mb;
  return {
    raw_input_mb,
    processed_out_mb,
    reduction_pct,
    high_pri_sent,
    low_pri_deferred,
    detections,
    conv_total_mb,
    edge_total_mb,
  };
}

export function formatMB(mb) {
  if (!isFinite(mb)) return "0 MB";
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(1)} MB`;
}

export function formatT(sec) {
  const s = Math.floor(sec);
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

// Severity colour
export const SEV_COLOR = {
  critical: "#EF4444",
  high: "#F59E0B",
  medium: "#22D3EE",
  low: "#64748B",
};
